import { useEffect } from 'react';
import { useParams } from 'react-router';

import { Spinner } from '@/components/ui/spinner';
import { useResolveShare } from '@/hooks/useShare';

/**
 * Resolves an opaque share link (`/s/:token`) to a presigned download.
 *
 * On `200 { url }` the browser is replaced with the presigned URL (a direct
 * download). On `404` — or any error — a generic "This link is unavailable"
 * page renders. The page reveals NOTHING about the token, tenant, or filename:
 * not-found, expired, revoked, and unauthorized are byte-identical here because
 * the backend returns the same generic 404 for all of them.
 *
 * The route is behind the Cognito auth gate (see AppRoute.tsx) — an
 * unauthenticated visitor is bounced to /login first and lands back here.
 */
export function ShareResolve() {
  const { token } = useParams<{ token: string }>();
  const { data, isPending, isError } = useResolveShare(token);

  useEffect(() => {
    if (data?.url) {
      // Hard redirect to the presigned URL — replace() so the share link isn't
      // left in history.
      window.location.replace(data.url);
    }
  }, [data?.url]);

  // While resolving (or immediately after a successful resolve, before the
  // redirect commits) show a neutral loading state — no token/filename echoed.
  if (isPending || data?.url) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spinner centered size="xl" />
      </div>
    );
  }

  // 404 / error / unauthorized — one generic message for every negative branch.
  if (isError || !data?.url) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-2 px-4 text-center">
        <h1 className="text-chariot-text-primary text-lg font-semibold">
          This link is unavailable
        </h1>
        <p className="text-chariot-text-secondary text-sm">
          The link may have expired or been revoked.
        </p>
      </div>
    );
  }

  return null;
}

export default ShareResolve;
